import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, PlayCircle, BookOpen, ArrowLeft, ArrowRight } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../../i18n/LanguageContext';
import { toYouTubeEmbed } from '../../lib/youtube';
import { isHtml, safeHtml } from '../../lib/richText';
import { QuizPanel } from './LessonExtraPanels';
import { LessonArticle } from './LessonArticle';

const NonVideoStage = ({ lesson }) => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const content = pickLocalized(lesson.content, lang);
  if (lesson.type === 'quiz') return (
    <div data-testid="learn-quiz-stage" className="sm:rounded-xl border-y sm:border border-slate-200 bg-slate-50/60 px-5 py-7 sm:p-8">
      <QuizPanel quizId={lesson.quiz_id} />
    </div>
  );
  if (lesson.article) return <LessonArticle article={lesson.article} />;
  if (lesson.article_id) return (
    <div data-testid="learn-article-stage" className="sm:rounded-xl border-y sm:border border-slate-200 bg-slate-50/60 px-5 py-7 sm:p-8 flex flex-wrap items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><BookOpen className="w-5 h-5" /></span>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-bold text-slate-900">{L('هذا الدرس مقال للقراءة', 'This lesson is an article')}</div>
        <div className="text-xs text-slate-500 mt-0.5">{L('افتح المقال لقراءة محتوى الدرس كاملاً', 'Open the article to read the full lesson')}</div>
      </div>
      <Link to={`/library/post/${lesson.article_id}`} data-testid="learn-article-open" className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors">{L('قراءة المقال', 'Read article')}<Arrow className="w-4 h-4" /></Link>
    </div>
  );
  return (
    <article data-testid="learn-text-stage" className="sm:rounded-xl border-y sm:border border-slate-200 bg-white px-5 py-6 sm:p-8">
      {content ? (isHtml(content)
        ? <div className="rich-content text-slate-700 text-[15px] leading-loose" dangerouslySetInnerHTML={{ __html: safeHtml(content) }} />
        : <p className="text-slate-700 text-[15px] leading-loose whitespace-pre-line">{content}</p>)
        : <p className="text-slate-400 text-sm text-center py-6">{L('لا يوجد محتوى لهذا الدرس بعد', 'No content for this lesson yet')}</p>}
    </article>
  );
};

export const LessonPlayer = ({ lesson, locked, onEnded }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const embed = lesson?.youtube ? toYouTubeEmbed(lesson.youtube) : '';
  const isYT = !!embed && !lesson?.video_url;
  if (lesson && !locked && (lesson.type === 'text' || lesson.type === 'quiz')) return <NonVideoStage lesson={lesson} />;
  return (
    <div data-testid="learn-player" className="relative w-full aspect-video sm:rounded-xl overflow-hidden bg-slate-950">
      {!lesson ? null : locked ? (
        <div data-testid="learn-player-locked" className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-slate-900 to-blue-950 text-white">
          <span className="w-12 h-12 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center mb-3"><Lock className="w-6 h-6" /></span>
          <h3 className="text-base sm:text-lg font-bold mb-1">{L('هذا الدرس للمشتركين', 'This lesson is for subscribers')}</h3>
          <p className="text-slate-300 text-xs sm:text-sm mb-4">{L('اشترك لمشاهدة جميع دروس الكورس', 'Subscribe to watch every lesson')}</p>
          <Link to="/subscription" data-testid="learn-subscribe-btn" className="btn-subscribe h-9 px-4 text-sm"><Crown className="w-4 h-4" />{L('عرض الباقات', 'View plans')}</Link>
        </div>
      ) : lesson.video_url ? (
        <video key={lesson.id} data-testid="learn-video" src={lesson.video_url} controls playsInline autoPlay onEnded={onEnded} className="absolute inset-0 w-full h-full bg-black" />
      ) : isYT ? (
        <iframe key={lesson.id} data-testid="learn-youtube" src={embed} title={pickLocalized(lesson.title, lang)} className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2 text-sm"><PlayCircle className="w-10 h-10" />{L('لا يوجد فيديو لهذا الدرس', 'No video for this lesson')}</div>
      )}
    </div>
  );
};
